import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Right-hand side sheet (DESIGN.md › System Drawers & Modals). Esc, the ✕ button and
 * the backdrop all call `onRequestClose`, so the caller can confirm unsaved changes.
 * Focus moves into the drawer on open, is trapped while open, and returns afterwards.
 */
export default function Drawer({ open, title, subtitle, onRequestClose, footer, children, labelledBy }) {
  const titleId = useId();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const returnTo = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      returnTo?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e) => {
    // Ignore events bubbling (through React portals) from dialogs opened on top
    if (!panelRef.current?.contains(e.target)) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      onRequestClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)];
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.35)] backdrop-blur-[2px]" onClick={onRequestClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="relative h-full w-full sm:max-w-2xl bg-surface-container-lowest shadow-[0_20px_25px_-5px_rgba(15,23,42,0.12),0_8px_10px_-6px_rgba(15,23,42,0.06)] flex flex-col outline-none"
      >
        <header className="px-6 py-5 bg-surface-container-low/60 border-b border-surface-container flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id={titleId} className="flex items-center gap-2 text-xl font-bold text-primary tracking-tight">
              <span className="w-2.5 h-2.5 rounded-full bg-secondary shrink-0" aria-hidden="true" />
              {title}
            </h2>
            {subtitle && <p className="mt-1 text-xs text-on-surface-variant">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onRequestClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
          >
            <Icon name="close" size={22} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer className="px-6 py-4 border-t border-surface-container bg-surface-container-lowest flex flex-wrap items-center justify-between gap-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
